import type { ModuleManifest, Session } from "@crialume/sdk";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import type { Kernel } from "../kernel";

const KernelContext = createContext<Kernel | null>(null);
KernelContext.displayName = "Kernel";

export function KernelProvider({ kernel, children }: { kernel: Kernel; children: ReactNode }) {
  return <KernelContext.Provider value={kernel}>{children}</KernelContext.Provider>;
}

/** Acesso ao kernel — APENAS para o shell. Módulos usam `useModuleContext()` do sdk. */
export function useKernel(): Kernel {
  const kernel = useContext(KernelContext);
  if (!kernel) throw new Error("useKernel() precisa estar dentro de <KernelProvider>.");
  return kernel;
}

export function useAuthSession(): Session | null {
  const { auth } = useKernel();
  const subscribe = useCallback((onChange: () => void) => auth.subscribe(onChange), [auth]);
  return useSyncExternalStore(subscribe, auth.getSession, auth.getSession);
}

/** Versão do registry: muda a cada registro/carga/erro de módulo. */
export function useRegistryVersion(): number {
  const { registry } = useKernel();
  const subscribe = useCallback((onChange: () => void) => registry.subscribe(onChange), [registry]);
  const getVersion = useCallback(() => registry.getVersion(), [registry]);
  return useSyncExternalStore(subscribe, getVersion, getVersion);
}

/** Módulos acessíveis para a sessão atual, reativo a login/logout. */
export function useAccessibleModules(): ModuleManifest[] {
  const kernel = useKernel();
  const session = useAuthSession();
  const version = useRegistryVersion();

  return useMemo(
    () => kernel.registry.listAccessible({ session, config: kernel.config }),
    // `version` invalida o memo quando o registry muda.
    [kernel, session, version],
  );
}
